import { useState } from 'react';
import ChannelTypeIcon from '@/components/icons/ChannelTypeIcon';
import { getChannelTypeTitle } from '@/lib/utils/channelPlatformMeta';

interface PlatformIconGridProps {
  types: string[];
  isActive: (type: string) => boolean;
  onToggle: (type: string) => void;
  onToggleAll?: () => void;
  allActive?: boolean;
  iconSize?: number;
  className?: string;
  toggleAllLabel?: (allActive: boolean) => string;
}

export default function PlatformIconGrid({
  types,
  isActive,
  onToggle,
  onToggleAll,
  allActive,
  iconSize = 16,
  className,
  toggleAllLabel,
}: PlatformIconGridProps) {
  const [hoveredType, setHoveredType] = useState<string | null>(null);

  const defaultToggleAllLabel = (active: boolean) => (active ? 'Deselect all' : 'Select all');
  const getLabel = toggleAllLabel ?? defaultToggleAllLabel;

  return (
    <div className={className}>
      <div className="flex flex-wrap gap-1">
        {types.map((type) => {
          const active = isActive(type);
          return (
            <button
              key={type}
              type="button"
              onClick={() => onToggle(type)}
              onMouseEnter={() => setHoveredType(type)}
              onMouseLeave={() => setHoveredType(null)}
              aria-label={getChannelTypeTitle(type)}
              aria-pressed={active}
              className={`relative w-7 h-7 rounded-ggc-sm border flex items-center justify-center transition-all ${
                active
                  ? 'border-ggc-cta-blue bg-ggc-blue-light opacity-100'
                  : 'border-ggc-border-gray bg-white opacity-40 hover:opacity-70'
              }`}
            >
              <ChannelTypeIcon type={type} size={iconSize} tooltip={null} />
              {active && (
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-ggc-pill flex items-center justify-center">
                  <i className="fa fa-check text-white" style={{ fontSize: 7 }} />
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="mt-1 flex items-center justify-between h-4">
        <span className="ggc-text-caption-s text-ggc-text-gray truncate">
          {hoveredType ? getChannelTypeTitle(hoveredType) : ''}
        </span>
        {onToggleAll && (
          <button
            type="button"
            className="text-ggc-text-gray hover:text-ggc-cta-blue transition-colors shrink-0 ml-2 ggc-text-caption-s"
            onClick={onToggleAll}
          >
            {getLabel(!!allActive)}
          </button>
        )}
      </div>
    </div>
  );
}
